import VideoLayout from 'util/UI/videolayout/VideoLayout';
import { MEDIA_TYPE } from '../base/media/constants';
import { getTrackByMediaTypeAndParticipant } from '../base/tracks/functions';

import {
  SET_BLUR_CONTENT_WARNING,
  SELECT_ODD_ROTATION,
  SET_SEE_WHAT_IS_BEING_SHARED,
  SET_SENSORING_DEBUG_MODE,
  UPDATE_LAST_LARGE_VIDEO_MEDIA_EVENT
} from './actionTypes';

import {
  getLocalParticipant,
  getLocalScreenShareParticipant,
  getParticipantById,
  getPinnedParticipant,
  getRemoteParticipants,
  getVideoDeviceParticipants
} from '../base/participants/functions';
import { pinParticipant } from '../base/participants/actions';
import { isStageFilmstripAvailable } from '../filmstrip/functions';
import { fetchUserInfo } from '../login/actions';
import { getAutoPinSetting } from '../video-layout';
import { getRoomName } from '../base/conference/functions';
import {
  SELECT_LARGE_VIDEO_PARTICIPANT,
  SET_LARGE_VIDEO_DIMENSIONS,
  UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION
} from './actionTypes';
import utils from 'util/utils';

/**
 * Reset active rotation timers and, optionally, re-elect the large-video participant.
 *
 * @note *draft: written by Codex*
 * @param {boolean} [reload=false] - When true, triggers `selectParticipantInLargeVideo`
 * after cleanup to immediately refresh the large video selection.
 * @returns {Function}
 */
export function clearParticipantRotation(reload = false) {
  return (dispatch) => {
    if (window.videoRotationInterval) {
      clearInterval(window.videoRotationInterval);
      window.videoRotationInterval = undefined;
    }
    if (window.remoteThumbClickTimeout) {
      clearTimeout(window.remoteThumbClickTimeout);
      window.remoteThumbClickTimeout = undefined;
    }
    if (reload) dispatch(selectParticipantInLargeVideo());
  };
}

/**
 * Ensure the large video points at a valid participant, re-electing when the target leaves or is invalid.
 *
 * @note *draft: written by Codex*
 * @param {string} id - Participant identifier tied to the triggering event.
 * @param {boolean} left - Indicates whether the participant just left the conference.
 * @returns {Function}
 */
export function checkParticipantInLargeVideo(id, left) {
  return (dispatch, getState) => {
    const state = getState();
    const { participantId } = state['features/large-video'];
    if (participantId === id) {
      if (left) dispatch(clearParticipantRotation(true));
      else {
        const participant = getParticipantById(state, id);
        if (
          participant &&
          (participant.fakeParticipant ||
            participant.local ||
            participant.customIsVideoAvailable === 'off' ||
            participant.customSleepModeStats === 'on' ||
            participant.customUserExternal === 'on' ||
            participant.customUserPrivacy === 'on')
        ) {
          dispatch(clearParticipantRotation(true));
        }
      }
    } else {
      dispatch(selectParticipantInLargeVideo());
    }
  };
}

/**
 * Action to select the participant to be displayed in LargeVideo based on the
 * participant id provided. If a participant id is not provided, the LargeVideo
 * participant will be selected based on a variety of factors: If there is a
 * dominant or pinned speaker, or if there are remote tracks, etc.
 *
 * @param {string} [id] - Participant id to permanant pin; omitted for temporary pinning.
 * @param {boolean} [pinned=true] - Whether the request is to pin (true) or unpin (false).
 * Frequently run as a cleanup to pin no-one (id=undefined, pinned=true) and start/continue rotation.
 * @returns {Function}
 */
export function selectParticipantInLargeVideo(id, pinned = true) {
  return (dispatch, getState) => {
    const state = getState();
    if (!state['features/login'].user) {
      return;
    }
    if (isStageFilmstripAvailable(state, 1)) {
      if (window.videoRotationInterval) {
        clearInterval(window.videoRotationInterval);
        window.videoRotationInterval = undefined;
      }
      if (window.remoteThumbClickTimeout) {
        clearTimeout(window.remoteThumbClickTimeout);
        window.remoteThumbClickTimeout = undefined;
      }
      window.remoteThumbClickTimeoutChanging = undefined;
      // console.log('isStageFilmstripAvailable', id);
      return;
    }

    // Keep Etherpad open.
    if (state['features/etherpad'].editing) {
      return;
    }

    // const participantId = participant ?? _electParticipantInLargeVideo(state);
    const { permanentPinnedParticipant } = state['features/base/participants'];
    if (id || permanentPinnedParticipant) {
      if (window.videoRotationInterval) {
        clearInterval(window.videoRotationInterval);
        window.videoRotationInterval = undefined;
      }
      if (window.remoteThumbClickTimeout) {
        clearTimeout(window.remoteThumbClickTimeout);
        window.remoteThumbClickTimeout = undefined;
        window.remoteThumbClickTimeoutChanging = undefined;
      }
      if (pinned === true) {
        dispatch(selectLargeVideo(id ? id : permanentPinnedParticipant));
      } else if (pinned === false) {
        dispatch(
          startLargeVideoRotation(window.lastPinnedNonRemoteParticipant)
        );
      }
      return;
    }
    // If a participant is pinned, they will be shown in the LargeVideo (regardless of whether they are local or
    // remote) when the filmstrip on stage is disabled.
    let participant = getPinnedParticipant(state);
    if (participant) {
      // console.log('pinnedParticipant', participant.id);
      if (window.videoRotationInterval) {
        clearInterval(window.videoRotationInterval);
        window.videoRotationInterval = undefined;
      }
      if (window.remoteThumbClickTimeoutChanging) {
        clearTimeout(window.remoteThumbClickTimeout);
        window.remoteThumbClickTimeout = undefined;
        window.remoteThumbClickTimeoutChanging = undefined;
      }
      if (!window.remoteThumbClickTimeout) {
        window.remoteThumbClickTimeout = setTimeout(() => {
          clearTimeout(window.remoteThumbClickTimeout);
          window.remoteThumbClickTimeout = undefined;
          window.remoteThumbClickTimeoutChanging = undefined;
          dispatch(pinParticipant(null));
          dispatch(startLargeVideoRotation());
        }, 60 * 1000);
      }
      dispatch(selectLargeVideo(participant.id));
      return;
    }
    if (getVideoDeviceParticipants(state).length > 0)
      dispatch(startLargeVideoRotation());
    else {
      if (window.videoRotationInterval) {
        clearInterval(window.videoRotationInterval);
        window.videoRotationInterval = undefined;
      }
      if (window.remoteThumbClickTimeout) {
        clearTimeout(window.remoteThumbClickTimeout);
        window.remoteThumbClickTimeout = undefined;
      }
      dispatch({
        type: SELECT_LARGE_VIDEO_PARTICIPANT,
        participantId: null,
        largeVideoIds: state['features/large-video'].largeVideoIds
      });
    }
  };
}

export function startLargeVideoRotation(participantId) {
  return (dispatch, getState) => {
    const state = getState();
    const user = state['features/login'].user;
    const { rotationId, participantId: largeVideoParticipant } =
      state['features/large-video'];
    if (!window.videoRotationInterval) {
      // console.log('rotationMode 1', participantId);
      if (participantId) {
        dispatch(selectLargeVideo(participantId));
      } else if (utils.isRemoteLogin(user)) {
        let rotationTime = 10;
        const room = getRoomName(state);
        const options = user.device?.groups.filter(
          (item) => item._id._id === room
        );
        if (options?.length) rotationTime = options[0]._id?.rotationInterval;
        dispatch(electParticipantInLargeVideo());
        window.videoRotationInterval = setInterval(() => {
          dispatch(electParticipantInLargeVideo());
        }, utils.getTimeInMillis(rotationTime ?? 10, 1));
      } else if (rotationId) {
        const remotes = getRemoteParticipants(state);
        const result = [...remotes].find(
          // eslint-disable-next-line no-unused-vars
          ([key, val]) => val.customUserid === rotationId
        );
        if (result?.length > 0) dispatch(selectLargeVideo(result[0]));
      } else if (
        !isStageFilmstripAvailable(state, 1) &&
        !largeVideoParticipant
      ) {
        dispatch(electParticipantInLargeVideo());
      }
    }
  };
}
export function selectLargeVideo(participantId, renew = false) {
  return (dispatch, getState) => {
    const state = getState();
    // console.log('selectLargeVideo', participantId);
    const largeVideo = state['features/large-video'];
    // const remoteScreenShares = state['features/video-layout'].remoteScreenShares;
    // let latestScreenshareParticipantId;

    // if (remoteScreenShares && remoteScreenShares.length) {
    //   latestScreenshareParticipantId =
    //     remoteScreenShares[remoteScreenShares.length - 1];
    // }

    // When trying to auto pin screenshare, always select the endpoint even though it happens to be
    // the large video participant in redux (for the reasons listed above in the large video selection
    // logic above). The auto pin screenshare logic kicks in after the track is added
    // (which updates the large video participant and selects all endpoints because of the auto tile
    // view mode). If the screenshare endpoint is not among the forwarded endpoints from the bridge,
    // it needs to be selected again at this point.

    if (
      participantId &&
      participantId !== largeVideo.participantId &&
      participantId !== 'whiteboard' &&
      participantId !== 'local'
    ) {
      const p = getParticipantById(state, participantId);
      const profiles = state['features/login'].profiles ?? {};
      // const { conference } = state['features/base/conference'];
      // conference.rtc?.selectEndpoints([ participantId ]);

      if (
        p?.customUserid &&
        p?.customUserid !== 'off' &&
        profiles &&
        !profiles[p.customUserid]
      ) {
        dispatch(fetchUserInfo(p?.customUserid));
      }
      const largeVideoIds = renew ? new Set() : largeVideo.largeVideoIds;
      if (!p?.isRemote) largeVideoIds.add(participantId);
      dispatch({
        type: SELECT_LARGE_VIDEO_PARTICIPANT,
        participantId,
        largeVideoIds
      });
    }
  };
}
/**
 * Updates the currently seen resolution of the video displayed on large video.
 *
 * @param {number} resolution - The current resolution (height) of the video.
 * @returns {{
 *     type: UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION,
 *     resolution
 * }}
 */
export function updateKnownLargeVideoResolution(resolution) {
  return {
    type: UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION,
    resolution
  };
}

/**
 * Sets the dimenstions of the large video in redux.
 *
 * @param {number} height - The height of the large video.
 * @param {number} width - The width of the large video.
 * @returns {{
 *     type: SET_LARGE_VIDEO_DIMENSIONS,
 *     height,
 *     width
 * }}
 */
export function setLargeVideoDimensions(height, width) {
  return {
    type: SET_LARGE_VIDEO_DIMENSIONS,
    height,
    width
  };
}

/**
 * Returns the most recent existing remote video track.
 *
 * @param {Track[]} tracks - All current tracks.
 * @private
 * @returns {(Track|undefined)}
 */
// eslint-disable-next-line no-unused-vars
function _electLastVisibleRemoteVideo(tracks) {
  // First we try to get most recent remote video track.
  for (let i = tracks.length - 1; i >= 0; --i) {
    const track = tracks[i];

    if (!track.local && track.mediaType === MEDIA_TYPE.VIDEO) {
      return track;
    }
  }
}

/**
 * Returns the identifier of the participant who is to be on the stage and
 * should be displayed in {@code LargeVideo}.
 *
 * @param {Object} state - The Redux state from which the participant to be
 * displayed in {@code LargeVideo} is to be elected.
 * @private
 * @returns {(string|undefined)}
 */
export function electParticipantInLargeVideo() {
  return (dispatch, getState) => {
    const state = getState();
    const user = state['features/login'].user;
    const oddRotation = state['features/large-video'].oddRotation;
    if (
      user?.device?.showDigitalSignage &&
      user?.device?.digitalSignage?.showByOddPairing &&
      !utils.isRemoteLogin(user)
    ) {
      if (oddRotation) return;
      dispatch({
        type: SELECT_ODD_ROTATION,
        active: true
      });
      return;
    } else if (oddRotation) {
      dispatch({
        type: SELECT_ODD_ROTATION,
        active: false
      });
    }
    const autoPinSetting = getAutoPinSetting();
    if (autoPinSetting) {
      // when the setting auto_pin_latest_screen_share is true as spot does, prioritize local screenshare
      if (autoPinSetting === true) {
        const localScreenShareParticipant =
          getLocalScreenShareParticipant(state);

        if (localScreenShareParticipant) {
          return dispatch(selectLargeVideo(localScreenShareParticipant.id));
        }
      }

      // Pick the most recent remote screenshare that was added to the conference.
      const remoteScreenShares =
        state['features/video-layout'].remoteScreenShares;

      if (remoteScreenShares?.length) {
        return dispatch(
          selectLargeVideo(remoteScreenShares[remoteScreenShares.length - 1])
        );
      }
    }

    // In case this is the local participant.
    let participant = undefined;

    // Last, select the participant that joined last (other than poltergist or other bot type participants).
    const participants = getVideoDeviceParticipants(state);
    const largeVideoIds = state['features/large-video'].largeVideoIds;
    for (const value of participants) {
      if (!largeVideoIds.has(value.id)) {
        participant = value;
        break;
      }
    }
    if (participant) {
      return dispatch(selectLargeVideo(participant.id));
    } else if (participants.length > 0) {
      return dispatch(selectLargeVideo(participants[0].id, true));
    }
    return dispatch(selectLargeVideo(getLocalParticipant(state)?.id));
  };
}

/**
 * Captures a screenshot of the video displayed on the large video.
 *
 * @returns {Function}
 */
export function captureLargeVideoScreenshot() {
  return (dispatch, getState) => {
    const state = getState();
    const largeVideo = state['features/large-video'];
    const promise = Promise.resolve();

    if (!largeVideo) {
      return promise;
    }
    const tracks = state['features/base/tracks'];
    const participantTrack = getTrackByMediaTypeAndParticipant(
      tracks,
      MEDIA_TYPE.VIDEO,
      largeVideo.participantId
    );

    // Participants that join the call video muted do not have a jitsiTrack attached.
    if (!(participantTrack && participantTrack.jitsiTrack)) {
      return promise;
    }
    const videoStream = participantTrack.jitsiTrack.getOriginalStream();

    if (!videoStream) {
      return promise;
    }

    // Get the video element for the large video, cast HTMLElement to HTMLVideoElement to make flow happy.
    /* eslint-disable-next-line no-extra-parens*/
    const videoElement = document.getElementById('largeVideo');

    if (!videoElement) {
      return promise;
    }

    // Create a HTML canvas and draw video on to the canvas.
    const [track] = videoStream.getVideoTracks();
    const { height, width } = track.getSettings() ?? track.getConstraints();
    const canvasElement = document.createElement('canvas');
    const ctx = canvasElement.getContext('2d');

    canvasElement.style.display = 'none';
    canvasElement.height = parseInt(height, 10);
    canvasElement.width = parseInt(width, 10);
    ctx.drawImage(videoElement, 0, 0);
    const dataURL = canvasElement.toDataURL('image/png', 1.0);

    // Cleanup.
    ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
    canvasElement.remove();

    return Promise.resolve(dataURL);
  };
}

/**
 * Resizes the large video container based on the dimensions provided.
 *
 * @param {number} width - Width that needs to be applied on the large video container.
 * @param {number} height - Height that needs to be applied on the large video container.
 * @returns {Function}
 */
export function resizeLargeVideo(width, height) {
  return (dispatch, getState) => {
    const state = getState();
    const largeVideo = state['features/large-video'];

    if (largeVideo) {
      const largeVideoContainer = VideoLayout.getLargeVideo();

      largeVideoContainer.updateContainerSize(width, height);
      largeVideoContainer.resize();
    }
  };
}

/**
 * Updates the last media event received for the large video.
 *
 * @param {string} name - The current media event name for the video.
 * @returns {{
 *     type: UPDATE_LAST_LARGE_VIDEO_MEDIA_EVENT,
 *     name
 * }}
 */
export function updateLastLargeVideoMediaEvent(name) {
  return {
    type: UPDATE_LAST_LARGE_VIDEO_MEDIA_EVENT,
    name
  };
}

/**
 * Updates the value used to display what is being shared.
 *
 * @param {boolean} seeWhatIsBeingShared - The current value.
 * @returns {{
 *     type: SET_SEE_WHAT_IS_BEING_SHARED,
 *     seeWhatIsBeingShared: boolean
 * }}
 */
export function setSeeWhatIsBeingShared(seeWhatIsBeingShared) {
  return {
    type: SET_SEE_WHAT_IS_BEING_SHARED,
    seeWhatIsBeingShared
  };
}

/**
 * Updates the value used to display what is being shared.
 *
 * @param {boolean} id - The participant id.
 * @returns {{
 *     type: SET_BLUR_CONTENT_WARNING,
 *     id: string
 * }}
 */
export function setBlurContentWarning(id) {
  return {
    type: SET_BLUR_CONTENT_WARNING,
    id
  };
}

/**
 * Updates the value used to display what is being shared.
 *
 * @param {boolean} id - The participant id.
 * @returns {{
 *     type: SET_BLUR_CONTENT_WARNING,
 *     id: string
 * }}
 */
export function setSensoringDebugMode(mode) {
  return {
    type: SET_SENSORING_DEBUG_MODE,
    mode
  };
}
